export type Predicate<Context> = (context: Context) => boolean;

export function and<Context>(...predicates: Predicate<Context>[]): Predicate<Context> {
  return (context) => predicates.every((predicate) => predicate(context));
}

export function or<Context>(...predicates: Predicate<Context>[]): Predicate<Context> {
  return (context) => predicates.some((predicate) => predicate(context));
}

export type FeatureFlagContext = {
  flagStore: { ready: boolean; flags: Record<string, boolean | undefined> };
};

export function requireFeatureFlag(featureFlag: string): Predicate<FeatureFlagContext> {
  return ({ flagStore }) => Boolean(flagStore.ready && flagStore.flags[featureFlag]);
}

export function omitWhenFeatureFlag(featureFlag: string): Predicate<FeatureFlagContext> {
  return ({ flagStore }) => Boolean(flagStore.ready && !flagStore.flags[featureFlag]);
}

type PredicateItem<Context> = { predicate?: Predicate<Context> };

export function predicateFilter<Context, Item extends PredicateItem<Context>>(
  items: Item[] | null | undefined,
  context: Context,
): Item[];

export function predicateFilter<Context, Item>(
  items: Item[] | null | undefined,
  context: Context,
  mapper: (item: Item) => PredicateItem<Context>,
): Item[];

export function predicateFilter<Context, Item>(
  items: Item[] | null | undefined,
  context: Context,
  mapper?: (item: Item) => PredicateItem<Context>,
): Item[] | undefined {
  return items?.filter((item) => {
    const { predicate } = mapper ? mapper(item) : (item as PredicateItem<Context>);

    return !predicate || predicate(context);
  });
}
